import React, { useState, useRef } from 'react';
import { 
  Box, 
  Card, 
  CardContent,
  Button,
  ButtonGroup,
} from '@mui/material';
import { ApiCostsDataResponse } from '../../types/api';
import { ExportService } from '../../services/exportService';
import { GenericTable } from '../../components/GenericTable';
import { GenericBarChart } from '../../components/GenericBarChart';
import { GenericCurveChart } from '../../components/GenericCurveChart';
import {
  transformCostsTableData,
  costsDailyTableColumns,
  transformCostsChartData,
  transformCostsCurveData,
  getCostsRowBackgroundColor,
  getCostsRowTextColor,
  getCostsCellBackgroundColor,
  getCostsCellTextColor,
  costsWeeklyTableColumns
} from '../../transforms/costsTransforms';
import { FilterControls } from '../../components/FilterControls';
import { useSearchParams } from 'react-router-dom';

interface CostsChartViewProps {
  data: ApiCostsDataResponse;
  period: "all" | "year" | "day" | "week";
  setPeriod: (value: "all" | "year" | "day" | "week") => void;
}

const exportService = new ExportService();

export const CostsChartView: React.FC<CostsChartViewProps> = ({ data, period, setPeriod }) => {
  const [searchParams] = useSearchParams();
  const hideDownloadOptions = searchParams.get('hideDownloadOptions') || 'false';
  const [currentView, setCurrentView] = useState<'chart' | 'table' | 'curve'>('chart');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });

  const chartRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const curveRef = useRef<HTMLDivElement>(null);

  const tableData = transformCostsTableData(data, dateRange, period as any);
  const chartData = transformCostsChartData(data, dateRange, period as any);
  const curveData = transformCostsCurveData(data, dateRange, period as any);
  const tableColumns = period === 'week' ? costsWeeklyTableColumns : costsDailyTableColumns;

  const handleExportExcel = async () => {
    try {
      await exportService.exportToExcelV2(
        tableColumns.map((h: any) => h.label),
        tableData.map((data) => 
          [data.Fecha, `${data['Costo Planeado']}`, `${data['Costo Ejecutado']}`, `${data['Diferencia']}`]
        ),
        `${data.Name}_costos`
      )
    } catch (error) {
      alert('Error al exportar a Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      let elementRef = chartRef;
      if (currentView === 'curve') elementRef = curveRef;
      if (!elementRef.current) {
        return;
      }
      await exportService.exportCostsToPDF(data, elementRef.current);
    } catch (error) {
      alert('Error al exportar a PDF');
    }
  };

  return (
    <Box sx={{ width: '100%', margin: '0 auto' }}>
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '1rem', pb: '1rem' }}>
            <strong>{`${data.Name} [${data.UnitMeasurement}]`}</strong>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '0.75rem' }}>
            <Box>
              <strong>Inicio:</strong> {data.StartDate} | 
              <strong> Fin:</strong> {data.EndDate} | 
              <strong> Duración:</strong> {data.Duration}d |
            </Box>
            <Box>
              <strong>Total:</strong> {data.Metered} | 
              <strong> Ejecutado:</strong> {data.MeteredExcuted} | 
              <strong> Por ejecutar:</strong> {data.MeteredBalance} | 
              <strong> Costo Presupuestado:</strong> {data.BudgetCost} |
              <strong> Costo Planeado:</strong> {data.PlannedCost}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <FilterControls
        showAvailableOptions={['day', 'week']}
        defaultViewLevel='day'
        minDate={data?.StartDate}
        maxDate={data.EndDate}
        viewLevel={period}
        onViewLevelChange={setPeriod}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        showAllOption={currentView === 'table'}
      />

      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <ButtonGroup size="small" variant="outlined">
              <Button 
                onClick={() => setCurrentView('chart')}
                variant={currentView === 'chart' ? 'contained' : 'outlined'}
              >
                Gráfico
              </Button>
              <Button 
                onClick={() => setCurrentView('curve')}
                variant={currentView === 'curve' ? 'contained' : 'outlined'}
              >
                Curva S
              </Button>
              <Button 
                onClick={() => setCurrentView('table')}
                variant={currentView === 'table' ? 'contained' : 'outlined'}
              >
                Tabla
              </Button>
            </ButtonGroup>

            {hideDownloadOptions === 'false' && <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              {currentView === 'table' && <Button size="small" variant="outlined" onClick={handleExportExcel}>
                Excel
              </Button>}
              {currentView !== 'table' && <Button size="small" variant="outlined" onClick={handleExportPDF}>
                PDF
              </Button>}
            </Box>}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          {currentView === 'chart' && (
            <div ref={chartRef}>
              <GenericBarChart
                labels={chartData.labels}
                datasets={chartData.datasets}
                title={`Costos - ${period === 'week' ? 'Semanas' : 'Días'}`}
                yAxisTitle="Costo"
                maxValue={chartData.maxValue}
                shouldUseHorizontalLabels={chartData.shouldUseHorizontalLabels}
                height={400}
                options={chartData.customOptions}
              />
            </div>
          )}
          
          {currentView === 'table' && (
            <div ref={tableRef}>
            <GenericTable
              data={tableData}
              columns={tableColumns}
              showHierarchy={false}
              getRowBackgroundColor={getCostsRowBackgroundColor}
              getRowTextColor={getCostsRowTextColor}
              getCellBackgroundColor={getCostsCellBackgroundColor}
              getCellTextColor={getCostsCellTextColor}
            />
            </div>
          )}
          
          {currentView === 'curve' && (
            <div ref={curveRef}>
              <GenericCurveChart
                labels={curveData.labels}
                datasets={curveData.datasets}
                title="Curva S - Costos Acumulados"
                yAxisTitle="Costo Acumulado"
                maxValue={curveData.maxValue}
                shouldUseHorizontalLabels={curveData.shouldUseHorizontalLabels}
                height={400}
                options={chartData.customOptions}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};